/**
 * Bridge (GDD §3.6: steel truss spans on concrete piers, asphalt deck with dashed centre line, rails, lamp posts,
 * parked trucks; span = full river width + 88 m). Ported verbatim (concept L508–L550). Each span is one merged,
 * vertex-coloured mesh so it can fall and splash separately (GDD §3.7).
 */
import { BoxGeometry, Color, CylinderGeometry, Group, Mesh, Vector3, type Material } from 'three';
import type { Rng } from '../../core/rng.ts';
import { box, merge, mesh, type Part } from '../../world/geometry.ts';
import type { Materials } from '../materials.ts';

export interface BridgeParts {
  segs: Group[];
  seg: number;
  n: number;
}

const col = (hex: number) => new Color(hex);

export function buildBridge(span: number, deckY: number, M: Materials, rand: Rng): Group & { userData: BridgeParts } {
  const root = new Group();
  const n = Math.max(7, Math.round(span / 12));
  const seg = span / n;
  const segs: Group[] = [];
  const concrete = col(0x8e9092);
  const conc2 = col(0xa6a8a8);
  const asphalt = col(0x2f3134);
  const line = col(0xe8d58a);
  const steel = col(0x4d5a5e);
  const rail = col(0x7f8a8e);
  const truckCols = [0x8a2f2a, 0x3d5a7a, 0xc9c3b4, 0x4b5d3a, 0xb07a2a];
  for (let i = 0; i < n; i++) {
    const p: Part[] = [];
    const L = seg - 0.06;
    const dir = i % 2 ? 1 : -1;
    p.push(box(L, 1.8, 12, 0, -0.9, 0, concrete));
    p.push(box(L, 0.08, 9, 0, 0.04, 0, asphalt));
    p.push(box(L, 0.36, 1.4, 0, 0.18, 5.3, conc2));
    p.push(box(L, 0.36, 1.4, 0, 0.18, -5.3, conc2));
    for (let q = 0; q < 3; q++) p.push(box(1.6, 0.1, 0.18, -L / 2 + ((q + 0.5) * L) / 3, 0.1, 0, line));
    for (const z of [6.15, -6.15]) {
      p.push(box(L, 0.6, 0.6, 0, 7.5, z, steel));
      p.push(box(0.5, 7.5, 0.5, -L / 2 + 0.25, 3.75, z, steel));
      const dg = new BoxGeometry(0.36, Math.sqrt(L * L + 7.2 * 7.2), 0.36);
      dg.rotateZ(dir * Math.atan2(L, 7.2));
      dg.translate(0, 3.7, z);
      p.push({ geo: dg, color: steel });
      p.push(box(L, 0.14, 0.14, 0, 1.15, z > 0 ? 5.95 : -5.95, rail));
    }
    p.push(box(0.4, 0.4, 12.6, -L / 2 + 0.2, 7.5, 0, steel));
    const xb = new BoxGeometry(0.22, 0.22, Math.sqrt(12.3 * 12.3 + L * L));
    xb.rotateY(Math.atan2(L, 12.3) * dir);
    xb.translate(0, 7.55, 0);
    p.push({ geo: xb, color: steel });
    if (rand() < 0.42 && i > 0 && i < n - 1) {
      const tc = col(truckCols[Math.floor(rand() * truckCols.length)] ?? 0);
      const lane = rand() > 0.5 ? 2.2 : -2.2;
      const tx = (rand() - 0.5) * (L - 8);
      p.push(box(6.2, 2.6, 2.3, tx, 1.5, lane, tc));
      p.push(box(1.9, 2.0, 2.2, tx + (lane > 0 ? 4.2 : -4.2), 1.2, lane, col(0x2e3338)));
    }
    const geo = merge(p);
    const sg = new Group();
    sg.position.set(-span / 2 + seg * (i + 0.5), deckY, 0);
    const m = new Mesh(geo, M.bridge);
    m.castShadow = true;
    m.receiveShadow = true;
    sg.add(m);
    if (i % 2 === 0) {
      mesh(new CylinderGeometry(0.08, 0.1, 5, 6), M.dark, 0, 2.6, 5.7, sg);
      mesh(new BoxGeometry(0.9, 0.22, 0.4), M.lamp, 0, 5.1, 5.4, sg, false);
    }
    sg.userData.home = sg.position.clone() as Vector3;
    root.add(sg);
    segs.push(sg);
  }
  for (let i = 1; i < n; i++) {
    if (i % 2) continue;
    const pier = merge([
      box(3.2, deckY + 7, 9.5, 0, (deckY - 7) / 2 - 1.8, 0, concrete),
      box(4.2, 1.2, 11, 0, deckY - 2.4, 0, conc2),
    ]);
    const pm = new Mesh(pier, M.bridge as Material);
    pm.position.x = -span / 2 + seg * i;
    pm.castShadow = true;
    pm.receiveShadow = true;
    root.add(pm);
  }
  for (const s of [-1, 1]) {
    const ab = new Mesh(merge([box(14, deckY + 10, 16, 0, (deckY - 10) / 2 - 1.8, 0, concrete)]), M.bridge);
    ab.position.x = s * (span / 2 + 6.5);
    ab.castShadow = true;
    ab.receiveShadow = true;
    root.add(ab);
  }
  root.userData = { segs, seg, n } satisfies BridgeParts;
  return root as Group & { userData: BridgeParts };
}
